import { createSlice } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import drawerTransformer from 'helpers/navigation/drawerTransformer';
import topAppBarTransformer from 'helpers/navigation/topAppBarTransformer';
import drawerDefaultData from 'data/drawer';
import navigationDefaultData from 'data/navigation';
import partnerDrawer from 'data/partnerDrawer';
import partnerNavigation from 'data/partnerNavigation';
import sysAdminNavigation from 'data/sysAdminNavigation';
import rulesEvaluator from 'helpers/navigation/rulesEvaluator';
import partnerRulesEvaluator from 'helpers/navigation/partnerRulesEvaluator';
import {
  swipeclockUserSelector,
  selectPermissionsDictionary,
} from './swipeclockUserSlice';
import adminDrawer from 'data/sysAdminDrawer';

// The navigationSlice manages the global navigation state for the shell-UI app. It includes:
// - activeTabIndex: The index of the active tab
// - activeDrawer: The drawer currently displayed
// - companyDrawer: The drawer for the company
// - drawers: A dictionary of filtered drawer items
// - drawerIsOpen: A boolean indicating if the drawer is open
// - hasDrawer: A boolean indicating if a drawer is available
// - hasCompanyDrawer: A boolean indicating if a company drawer is available
// - siteId: A string representing the siteId for the current company
// - shouldRedirectToDashboard: A boolean indicating if there should be a redirection to the dashboard
// - topAppBar: An array of filtered topAppBar items
const navigationSlice = createSlice({
  name: 'navigation',
  initialState: {
    activeTabIndex: 0,
    activeDrawer: null,
    companyDrawer: null,
    drawers: {},
    drawerIsOpen: false,
    hasDrawer: false,
    hasCompanyDrawer: false,
    siteId: 0,
    shouldRedirectToDashboard: false,
    topAppBar: [],
    disabledTopAppBar: false,
    hideNavigationDrawer: false,
  },
  reducers: {
    setSiteId(state, action) {
      state.siteId = action.payload.siteId;
    },

    setActiveDrawer(state, action) {
      state.activeDrawer = action.payload.drawer;
    },

    setActiveTabIndex(state, action) {
      state.activeTabIndex = action.payload.index;
    },

    setDrawerOpen(state, action) {
      state.drawerIsOpen = !!action.payload.open;
    },

    setCompanyDrawer(state, action) {
      state.companyDrawer = action.payload.drawer;
    },

    setHasDrawer(state, action) {
      state.hasDrawer = !!action.payload.hasDrawer;
    },

    setHasCompanyDrawer(state, action) {
      state.hasCompanyDrawer = !!action.payload.hasDrawer;
    },

    setUsingCustomDrawer(state, action) {
      state.usingCustomDrawer = !!action.payload.usingCustomDrawer;
    },

    setNavigationTopAppBarOnly(state, action) {
      const { transformedTopAppBar } = action.payload;
      state.topAppBar = transformedTopAppBar;
      state.navigationReady = true;
    },

    setNavigationFrom(state, action) {
      const { transformedTopAppBar, transformedDrawer } = action.payload;
      state.topAppBar = transformedTopAppBar;
      state.drawers = transformedDrawer;
      state.navigationReady = true;
    },

    setUsingExternalDrawer(state, action) {
      state.usingExternalDrawer = !!action.payload.usingExternalDrawer;
    },

    setShouldRedirectToDashboard(state, action) {
      state.shouldRedirectToDashboard = !!action.payload.reidrect;
    },
    setDisableNavigation(state, action) {
      const { disabledTopAppBar, hideNavigationDrawer } = action.payload;
      state.disabledTopAppBar = !!disabledTopAppBar;
      state.hideNavigationDrawer = !!hideNavigationDrawer;
    },
  },
});

export function setActiveTab(order) {
  return async (dispatch) => {
    // Update tab right away
    dispatch(setActiveTabIndex({ index: order }));
  };
}

export function setWFHNavigation(masterData) {
  return async (dispatch, getState) => {
    const { features, roles, permissions } = masterData ?? {};
    const permissionsDictionary = selectPermissionsDictionary(getState());
    const evaluator = rulesEvaluator(
      features,
      roles,
      permissions,
      permissionsDictionary
    );
    const drawer = drawerTransformer(evaluator);
    const topAppBar = topAppBarTransformer(evaluator);
    dispatch(
      setNavigationFrom({
        transformedTopAppBar: topAppBar.transform(navigationDefaultData),
        transformedDrawer: drawer.transform(drawerDefaultData),
      })
    );

    dispatch(setCompanyDrawer({ drawer: null }));
    dispatch(setHasCompanyDrawer({ hasDrawer: false }));
  };
}

export function setPartnerNavigation(
  masterData,
  partner,
  updateDrawer = false
) {
  return async (dispatch) => {
    const { roles, permissions } = masterData ?? {};
    const evaluator = partnerRulesEvaluator(partner, roles, permissions);
    const topAppBar = topAppBarTransformer(evaluator);
    const drawer = drawerTransformer(evaluator);
    const transformedDrawer = drawer.transform(partnerDrawer);
    dispatch(
      setNavigationFrom({
        transformedTopAppBar: topAppBar.transform(partnerNavigation),
        transformedDrawer: transformedDrawer,
      })
    );

    dispatch(
      setNavigationTopAppBarOnly({
        transformedTopAppBar: topAppBar.transform(partnerNavigation),
      })
    );

    dispatch(setCompanyDrawer({ drawer: null }));
    dispatch(setHasCompanyDrawer({ hasDrawer: false }));
    // If we need to update the drawer, we need to set it as active
    if (updateDrawer && transformedDrawer?.partner?.[0]) {
      const activeDrawer = transformedDrawer.partner[0];
      dispatch(setActiveDrawer({ drawer: activeDrawer }));
    }
  };
}

// It's an external event but really this is just for companies
export function setExternalEventDrawer({ drawerName, drawerItems }) {
  return async (dispatch, _getState) => {
    // Find out if the company drawer has already been cached
    // const { hasCompanyDrawer } = getState().navigation;

    const drawer = drawerTransformer(alwaysShowEvaluator).transform({
      [drawerName]: drawerItems,
    });

    dispatch(setCompanyDrawer({ drawer }));
    dispatch(setHasCompanyDrawer({ hasDrawer: true }));

    if (drawerItems?.[0]?.siteId) {
      dispatch(setSiteId({ siteId: drawerItems[0].siteId }));
    }

    // Set the active drawer here if it's a company drawer. This is necessary because the drawer
    // needs to be updated when the "show drawer" event is called, and the drawer may not always be the same.
    // Previously, we set the active drawer in the loader like other pages, but this led to a bug where
    // pages would redirect to the companies-dashboard on page changes.
    // Since we cannot filter "show drawer" events, we need to set the active drawer here.
    if (drawer?.company?.[0]) {
      const activeDrawer = drawer.company[0];
      dispatch(setActiveDrawer({ drawer: activeDrawer }));
    }
  };
}

export function removeExternalEventDrawer() {
  return async (dispatch) => {
    dispatch(setCompanyDrawer({ drawer: null }));
    dispatch(setHasCompanyDrawer({ hasDrawer: false }));
  };
}

const alwaysShowEvaluator = {
  checkIfDrawerItemIncluded: () => true,
  checkIfTopAppBarItemIncluded: () => true,
};

export const {
  setSiteId,
  setActiveDrawer,
  setActiveTabIndex,
  setDrawerOpen,
  setHasDrawer,
  setCompanyDrawer,
  setHasCompanyDrawer,
  setNavigationFrom,
  setNavigationTopAppBarOnly,
  setShouldRedirectToDashboard,
  setDisableNavigation,
} = navigationSlice.actions;

export const navigationSelector = (state) => state.navigation;

// Assuming the state shape is something like: { navigation: { usingExternalDrawer, drawer } }

const selectNavigationState = (state) => state.navigation;

export const selectNavigationDetails = createSelector(
  [selectNavigationState],
  (navigation) => ({
    activeDrawer: navigation.activeDrawer,
    drawerIsOpen: navigation.drawerIsOpen,
    hasDrawer: navigation.hasDrawer,
    hasCompanyDrawer: navigation.hasCompanyDrawer,
    siteId: navigation.siteId,
    topAppBar: navigation.topAppBar,
    hide: navigation.hideNavigationDrawer,
  })
);

export const selectHasCompanyDrawer = (state) =>
  state.navigation.hasCompanyDrawer;

export const selectCompanyNavigationDetails = createSelector(
  [selectNavigationState],
  (navigation) => ({
    companyDrawer: navigation.companyDrawer,
    drawerIsOpen: navigation.drawerIsOpen,
    hasCompanyDrawer: navigation.hasCompanyDrawer,
    siteId: navigation.siteId,
    topAppBar: navigation.topAppBar,
    hide: navigation.hideNavigationDrawer,
  })
);

export const selectNavigationData = createSelector(
  [navigationSelector, swipeclockUserSelector],
  (navigation, swipeclockUser) => {
    const result = {
      ...navigation,
      disabledTopAppBar: navigation.disabledTopAppBar,
      productLevel: swipeclockUser.productLevel,
    };
    return result;
  }
);

export const selectTopAppBar = createSelector(
  [selectNavigationData],
  ({ activeTabIndex, topAppBar }) => ({ activeTabIndex, topAppBar })
);

export const selectHasPeopleTab = createSelector(
  [selectNavigationState],
  (navigation) => navigation.topAppBar?.some((tab) => tab.key === 'people')
);

export function setAdminNavigation(masterData, partner, updateDrawer = false) {
  return async (dispatch) => {
    const { roles, permissions } = masterData ?? {};
    const evaluator = partnerRulesEvaluator(partner, roles, permissions);
    const topAppBar = topAppBarTransformer(evaluator);
    const drawer = drawerTransformer(evaluator);
    const transformedAdminDrawer = drawer.transform(adminDrawer);
    const transformedPartnerDrawer = drawer.transform(partnerDrawer);
    dispatch(
      setNavigationFrom({
        transformedTopAppBar: topAppBar.transform(sysAdminNavigation),
        transformedDrawer: {
          ...transformedPartnerDrawer,
          ...transformedAdminDrawer,
        },
      })
    );

    dispatch(
      setNavigationTopAppBarOnly({
        transformedTopAppBar: topAppBar.transform(sysAdminNavigation),
      })
    );

    dispatch(setCompanyDrawer({ drawer: null }));
    dispatch(setHasCompanyDrawer({ hasDrawer: false }));
    // If we need to update the drawer, we need to set it as active
    if (updateDrawer && transformedAdminDrawer?.admin?.[0]) {
      const activeDrawer = transformedAdminDrawer.admin[0];
      dispatch(setActiveDrawer({ drawer: activeDrawer }));
    }
  };
}

export default navigationSlice.reducer;
